import {
  Accordion,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  Stack,
  useToast,
} from '@chakra-ui/react';
import MainCard from '../../../components/MainCard/Index';
import AccordionTemplate from '../../../components/AccordionTemplate/Index';
import InputTemplate from '../../../components/InputTemplate/Index';
import { IoIosEyeOff } from 'react-icons/io';
import inputMask from 'react-input-mask';
import * as Yup from 'yup';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useValidateCEP } from '../../../services/hooks/SignUp/useCepValidation';
import { IoSearchSharp } from 'react-icons/io5';
import { validateCPF } from '../../../utils/validateCPF';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useRequestGetState } from '../../../services/hooks/SignUp/useRequestGetState';
import { useRequestGetCity } from '../../../services/hooks/SignUp/useRequestGetCity';
import { useRequestGetDocumentType } from '../../../services/hooks/SignUp/useRequestGetDocumentType';
import { useRequestGetGender } from '../../../services/hooks/SignUp/useRequestGetGender';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequestGetMaritalStatus } from '../../../services/hooks/SignUp/useRequestGetMaritalStatus';
import { useRequestGetNationality } from '../../../services/hooks/SignUp/useRequestGetNationality';
import { useRequestGetEducationLevel } from '../../../services/hooks/SignUp/useRequestGetEducationLevel';
import { EFormatDate, formatDate } from '../../../utils/formatData';
import { ETypeError, switchMessageError, validateDate } from '../../../utils/validateDate';
import MessageError from '../../../components/MessageError';
import { TextXS } from '../../../components/Text/Index';
import { useCreateCandidate } from '../../../services/hooks/SignUp/useCreateCandidate';
import { ContextAuth } from '../../../contexts/Authentication';
import { DefaultButton } from '../../../components/ButtonDefault/Index';
import MessageForUser from '../../../components/pages/SelectiveProcess/MessageForUser';
import { useRequestGetRaces } from '../../../services/hooks/SignUp/useRequestGetRaces';
import ProfileMobilePage from '../../private/Profile/mobile';
import { useRequestGetGenderIdentity } from '../../../services/hooks/SignUp/useRequestGetGenderIdentity';

const SignUp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const selectiveProcessId = searchParams?.get('selective_process_id');
  const route = searchParams?.get('route');
  const is_foreign_url = searchParams?.get('is_foreign') === 'true';

  const { isMobile } = useContext(ContextAuth);

  const validateCEP = useValidateCEP();
  const toast = useToast();
  const [openTab, setOpenTab] = useState(0);
  const [maskPhone, setMaskPhone] = useState<boolean>(false);
  const getStates = useRequestGetState();
  const getDocumentType = useRequestGetDocumentType();
  const getGender = useRequestGetGender();
  const getGenderidentity = useRequestGetGenderIdentity();
  const getMaritalStatus = useRequestGetMaritalStatus();
  const getNationality = useRequestGetNationality();
  const getEducationLevel = useRequestGetEducationLevel();
  const getRaces = useRequestGetRaces();
  const [city, setCity] = useState<string>();
  const navigate = useNavigate();
  const maxDate = formatDate(new Date(Date.now()).toLocaleString().substring(0, 10), EFormatDate.US);
  const { mutateAsync, isPending } = useCreateCandidate();
  const { signIn } = useContext(ContextAuth);

  const PersonalData = {
    is_foreign: Yup.boolean(),
    passport_number: Yup.string()
      .uppercase()
      .when('is_foreign', {
        is: true,
        then: (schema) => schema.required('Campo obrigatório'),
      }),
    full_name: Yup.string()
      .required('Campo obrigatório')
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/, 'O nome não pode conter caracteres especiais')
      .test('nome-completo', 'Digite seu nome completo', (value) => {
        if (!value) return false; // garante que não seja vazio
        const parts = value.trim().split(/\s+/); // separa por espaços
        return parts.length >= 2; // precisa ter pelo menos duas partes
      }),
    // mother_name: Yup.string()
    //   .required('Campo Obrigatorio')
    //   .test('nome-completo-mae', 'Digite o nome completo', (value) => {
    //     if (!value) return false; // garante que não seja vazio
    //     const parts = value.trim().split(/\s+/); // separa por espaços
    //     return parts.length >= 2; // precisa ter pelo menos duas partes
    //   }),
    mother_name: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema.required('Campo Obrigatorio').test('nome-completo-mae', 'Digite o nome completo', (value) => {
          if (!value) return false; // garante que não seja vazio
          const parts = value.trim().split(/\s+/); // separa por espaços
          return parts.length >= 2; // precisa ter pelo menos duas partes
        }),
    }),
    cpf: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema
          .required('Campo obrigatório')
          .test('invalidCPF', 'CPF incompleto', (value) => {
            const cpf = value ?? '';
            const digits = cpf.replace(/\D/g, '');
            return digits.length >= 11;
          })
          .test('invalidCPF', 'Informe um CPF válido', (value) => {
            const cpf = value ?? '';
            const numbers = cpf.replace(/\D/g, '');
            if (numbers.length === 11) return validateCPF(numbers);
            else if (numbers.length > 11) return false;
            return true;
          }),
    }),
    date_of_birth: Yup.string().test('dataInvalid', (value, { createError, path }) => {
      const typeError = validateDate(value);

      if (typeError === ETypeError.noError) return true;

      return createError({
        message: switchMessageError(typeError),
        path: path,
      });
    }),
    education_level: Yup.string().required('Campo Obrigatorio'),
    gender: Yup.string().required('Campo Obrigatorio'),
    gender_identity: Yup.string().required('Campo Obrigatorio'),
    marital_status: Yup.string().required('Campo Obrigatorio'),
    race: Yup.string().required('Campo Obrigatorio'),
    email: Yup.string().required('Campo Obrigatorio').email('Preencha um e-mail válido').lowercase(),

    nationality_foreign: Yup.string().when('nationality', {
      is: (nationality: string) => {
        const apiNationality = getNationality?.data?.find((get) => get.id === nationality);
        if (apiNationality && apiNationality.name.toLowerCase().includes('outro')) return true;
      },
      then: (schema) => schema.required('O campo é Obrigatorio'),
    }),

    document_type: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    document_number: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    document_issuing_body: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'), //Orgão emissor
    }),

    document_issuing_body_uf: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    document_date_of_issuance: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema.test('insuanceInvalid', (value, { createError, path }) => {
          const typeError = validateDate(value);

          if (typeError === ETypeError.noError) return true;

          return createError({
            message: switchMessageError(typeError),
            path: path,
          });
        }),
    }),

    nationality: Yup.string().required('Campo Obrigatorio'),

    state_naturality: Yup.string().when('nationality', {
      is: (nationality: string) => {
        const apiNationality = getNationality?.data?.find((get) => get.id === nationality);
        if (apiNationality && !apiNationality.name.toLowerCase().includes('outro')) return true;
      },
      then: (schema) => schema.required('O campo é Obrigatorio'),
    }),

    city_naturality: Yup.string().when('nationality', {
      is: (nationality: string) => {
        const apiNationality = getNationality?.data?.find((get) => get.id === nationality);
        if (apiNationality && !apiNationality.name.toLowerCase().includes('outro')) return true;
      },
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),
  };

  const AddresAndContact = {
    address_zip_code: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) =>
        schema.test('validateZip', 'Cep inválido', (cep) => {
          const zipCode = cep ?? '';
          const numbers = zipCode.replace(/\D/g, '');
          return !(numbers.length < 8);
        }),
    }),

    address_street: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_number: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_complement: Yup.string(),

    address_neighborhood: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_state: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    address_city: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    cellphone: Yup.string().when('is_foreign', {
      is: false,
      then: (schema) => schema.required('Campo Obrigatorio'),
    }),

    phone: Yup.string(),
  };

  const Password = {
    password: Yup.string()
      .required('Campo Obrigatorio')
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
        'A senha deve ter no mínimo 8 caracteres e conter pelo menos uma letra e um número',
      ),
    confirm_password: Yup.string()
      .required('Campo Obrigatorio')
      .oneOf([Yup.ref('password')], 'As senhas devem coincidir'),
  };
  const schema = Yup.object().shape({
    ...PersonalData,
    ...Password,
    ...AddresAndContact,
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      cpf: (!is_foreign_url && sessionStorage.getItem('document_number')) || '',
      passport_number: (is_foreign_url && sessionStorage.getItem('document_number')) || '',
      is_foreign: is_foreign_url && sessionStorage.getItem('document_number') ? true : false,
    },
  });

  const verifyIsForeign = () => {
    return watch('is_foreign');
  };

  const RegisterAccount = (data: FieldValues) => {
    if (data) {
      mutateAsync(data)
        .then(() => {
          sessionStorage.removeItem('cpf');
          signIn(data?.email, data?.password).then(() => {
            toast({
              title: 'Sucesso',
              description: 'Cadastro realizado com sucesso',
              status: 'success',
              duration: 4000,
              isClosable: true,
            });
            if (route === '/processo-seletivo/inscricao') {
              navigate(route || '/painel/inscricoes', {
                state: { selectiveProcessId: selectiveProcessId },
              });
              return;
            }
            if (selectiveProcessId) {
              navigate(route || '/login', { state: selectiveProcessId });
            } else {
              navigate(route || '/login');
            }
          });
        })
        .catch((e) => {
          toast({
            title: 'Erro',
            description: e?.response?.data?.message || 'Erro na solicitação, tente novamente',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        });
    } else {
      toast({
        title: 'Erro',
        description: 'Dados invalidos, tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const getCity = useRequestGetCity(watch('address_state'));

  const getNaturality = useRequestGetCity(watch('state_naturality'));

  useEffect(() => {
    if (getCity.data && city) {
      getCity?.data?.forEach((localCity) => {
        const normalizedCity = localCity.name
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        const normalizedLocalCity = city
          ?.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        if (localCity?.id && normalizedCity == normalizedLocalCity) {
          setValue('address_city', localCity.id);
        }
      });
    }
  }, [getCity.data, city]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const error: number[] = [];
      Object.keys(errors).map((inputName) => {
        const pd = Object.keys(PersonalData).includes(inputName);
        if (pd) {
          error.push(0);
        }
        const pa = Object.keys(Password).includes(inputName);
        if (pa) {
          error.push(2);
        }
        const ac = Object.keys(AddresAndContact).includes(inputName);
        if (ac) {
          error.push(1);
        }
      });
      setOpenTab((value) => value + 1);
    }
  }, [errors]);

  const checkCep = async () => {
    // const validationZipCode = await trigger('address_zip_code');
    // eslint-disable-next-line no-constant-condition
    if (true) {
      const cep = getValues('address_zip_code');
      const zipCode = cep ?? '';
      const numbers = zipCode.replace(/\D/g, '');
      if (numbers?.length === 8) {
        validateCEP.mutateAsync(cep).then((data) => {
          if (data?.erro) {
            toast({
              title: 'Erro',
              description: 'CEP não encontrado',
              status: 'error',
              duration: 4000,
              isClosable: true,
            });
          } else {
            getStates?.data?.forEach((state) => {
              if (state.initials == data?.uf) {
                setValue('address_state', state.id || '');
              }
            });

            setValue('address_street', data?.logradouro || '');

            setValue('address_neighborhood', data?.bairro || '');
            if (data?.localidade) setCity(data?.localidade);
          }
        });
      } else {
        toast({
          title: 'Atenção',
          description: 'Por favor, preencha o CEP',
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
      }
    }
  };

  const getOtherNationality = (): boolean => {
    const nationality_id = watch('nationality');
    let response = false;
    getNationality.data?.forEach((nationality) => {
      if (nationality.id == nationality_id) {
        response = nationality.name.toLowerCase().includes('outro');
        return;
      }
    });

    return response;
  };

  const getIdNationality = (): boolean => {
    const nationality_id = watch('nationality');
    let response = false;
    getNationality.data?.forEach((nationality) => {
      if (nationality.id == nationality_id) {
        response = nationality.name.toLowerCase().includes('brasil');
        return;
      }
    });

    return response;
  };

  const backButton = () => {
    sessionStorage.removeItem('cpf');
    navigate(-1);
  };

  if (isMobile) return <ProfileMobilePage />;

  const is_foreign = watch('is_foreign');

  return (
    <>
      {validateCEP.isPending && (
        <Flex position={'absolute'} h="100dvh" w="100dvw" zIndex={999} justify="center" align="center">
          <Box position="absolute" bg="black" opacity={0.6} h="100%" w="100%"></Box>
          <Stack zIndex={999} color="white" align="center" gap={5}>
            <Spinner size="xl" />
            <TextXS>Carregando endereço</TextXS>
          </Stack>
        </Flex>
      )}

      <MainCard title="FORMULÁRIO DE CADASTRO">
        <MessageForUser
          mx={5}
          my={'10px 10px'}
          showIcon={false}
          informationText={`<p><strong>Prezado(a) candidato(a),</strong></p> </br>
            <p>O acesso à <strong>Área do Candidato</strong> deve ser feito com o <strong>e-mail e senha</strong> cadastrados no momento do registro. Para garantir uma comunicação eficiente durante o processo seletivo, <strong>certifique-se de que tem acesso ao e-mail informado</strong>, pois todas as notificações e atualizações serão enviadas para ele.</p>
            </br>
            <p><strong>Atenção ao cadastro!</strong></p>
            <p>O correto preenchimento do formulário é essencial, pois as informações fornecidas serão utilizadas ao longo do processo. Caso necessário, os dados cadastrados <strong>– exceto o CPF –</strong> poderão ser alterados posteriormente na <strong>Área do Candidato</strong>.</p>
            </br>
            <p>Fique atento(a) e boa sorte!</p>
            `}
        />
        <Accordion
          key={openTab}
          as="form"
          onSubmit={handleSubmit(RegisterAccount)}
          p={5}
          defaultIndex={[0, 1, 2]}
          allowMultiple
          display="flex"
          flexDir="column"
        >
          <AccordionTemplate gapPanel={5} title="DADOS PESSOAIS">
            <Flex mt={5} gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl w={['100%', '100%', '100%', '25%', '25%']} isRequired>
                <FormLabel>{is_foreign ? 'Passaporte' : 'CPF'}:</FormLabel>
                <Input
                  isRequired={false}
                  {...register(is_foreign ? 'passport_number' : 'cpf')}
                  textTransform={'uppercase'}
                  {...(!is_foreign && { as: inputMask, mask: '999.999.999-99' })}
                />
                <MessageError>{is_foreign ? errors.passport_number?.message : errors.cpf?.message}</MessageError>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Nome completo:</FormLabel>
                <Input isRequired={false} {...register('full_name')} />
                <MessageError>{errors.full_name?.message}</MessageError>
              </FormControl>
            </Flex>

            <Flex gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl isRequired w={['100%', '100%', '100%', '25%', '25%']}>
                <FormLabel>Data de nascimento:</FormLabel>
                <Input max={maxDate} type="date" isRequired={false} {...register('date_of_birth')} />
                <MessageError>{errors.date_of_birth?.message}</MessageError>
              </FormControl>
              <FormControl isRequired={!verifyIsForeign()}>
                <FormLabel>Nome da mãe:</FormLabel>
                <Input isRequired={false} {...register('mother_name')} />
                <MessageError>{errors.mother_name?.message}</MessageError>
              </FormControl>
            </Flex>

            {!verifyIsForeign() && (
              <Flex gap={5} flexDir={['column', 'column', 'column', 'column', 'row']}>
                <FormControl isRequired={!verifyIsForeign()}>
                  <FormLabel>Tipo de documento:</FormLabel>
                  <Select isRequired={false} {...register('document_type')}>
                    <option value="" hidden>
                      Selecione
                    </option>
                    {getDocumentType.data?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Select>
                  <MessageError>{errors.document_type?.message}</MessageError>
                </FormControl>

                <FormControl isRequired={!verifyIsForeign()}>
                  <FormLabel>Número do documento:</FormLabel>
                  <Input isRequired={false} {...register('document_number')} />
                  <MessageError>{errors.document_number?.message}</MessageError>
                </FormControl>

                <FormControl isRequired={!verifyIsForeign()}>
                  <FormLabel>Data de expedição:</FormLabel>
                  <Input type="date" isRequired={false} {...register('document_date_of_issuance')} />
                  <MessageError>{errors.document_date_of_issuance?.message}</MessageError>
                </FormControl>

                <FormControl isRequired={!verifyIsForeign()}>
                  <FormLabel>Órgão:</FormLabel>
                  <Input isRequired={false} {...register('document_issuing_body')} />
                  <MessageError>{errors.document_issuing_body?.message}</MessageError>
                </FormControl>

                <FormControl w={['100%', '100%', '100%', '50%', '50%']} isRequired={!verifyIsForeign()}>
                  <FormLabel>UF:</FormLabel>
                  <Select isRequired={false} {...register('document_issuing_body_uf')}>
                    <option value={''} hidden>
                      Selecione
                    </option>
                    {getStates?.data?.map((state) => (
                      <option value={state.id} key={state.id}>
                        {state.initials}
                      </option>
                    ))}
                  </Select>
                  <MessageError>{errors.document_issuing_body_uf?.message}</MessageError>
                </FormControl>
              </Flex>
            )}

            <Flex gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl isRequired>
                <FormLabel>Escolaridade:</FormLabel>
                <Select isRequired={false} {...register('education_level')}>
                  <option value="" hidden>
                    Selecione
                  </option>
                  {getEducationLevel?.data?.map((educationLevel) => (
                    <option key={educationLevel.id} value={educationLevel.id}>
                      {educationLevel.name}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.education_level?.message}</MessageError>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Sexo:</FormLabel>
                <Select isRequired={false} {...register('gender')}>
                  <option value="" hidden>
                    Selecione
                  </option>
                  {getGender?.data?.map((gender) => (
                    <option key={gender.id} value={gender.id}>
                      {gender.name}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.gender?.message}</MessageError>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Identidade de gênero:</FormLabel>
                <Select isRequired={false} {...register('gender_identity')}>
                  <option value="" hidden>
                    Selecione
                  </option>
                  {getGenderidentity?.data?.map((gender) => (
                    <option key={gender.id} value={gender.id}>
                      {gender.name}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.gender_identity?.message}</MessageError>
              </FormControl>
            </Flex>

            <Flex gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl isRequired>
                <FormLabel>Estado civil:</FormLabel>
                <Select isRequired={false} {...register('marital_status')}>
                  <option value="" hidden>
                    Selecione
                  </option>
                  {getMaritalStatus?.data?.map((maritalStatus) => (
                    <option key={maritalStatus.id} value={maritalStatus.id}>
                      {maritalStatus.name}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.marital_status?.message}</MessageError>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Raça:</FormLabel>
                <Select isRequired={false} {...register('race')}>
                  <option value="" hidden>
                    Selecione
                  </option>
                  {getRaces?.data?.map((race) => (
                    <option key={race?.id} value={race?.id}>
                      {race?.name}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.race?.message}</MessageError>
              </FormControl>
            </Flex>

            <Flex gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl isRequired>
                <FormLabel>Nacionalidade:</FormLabel>
                <Select
                  isRequired={false}
                  {...register('nationality', {
                    onChange: () => {
                      if (!getIdNationality()) {
                        setValue('city_naturality', undefined);
                        setValue('state_naturality', undefined);
                      }
                    },
                  })}
                >
                  <option value="" hidden>
                    Selecione
                  </option>
                  {getNationality.data?.map((nationality) => (
                    <option key={nationality.id} value={nationality.id}>
                      {nationality.name}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.nationality?.message}</MessageError>
              </FormControl>
              {getOtherNationality() && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Informe sua nacionalidade:</FormLabel>
                    <Input
                      isRequired={false}
                      borderTopRightRadius={0}
                      borderBottomRightRadius={0}
                      {...register('nationality_foreign')}
                    />
                    <MessageError>{errors.nationality_foreign?.message}</MessageError>
                  </FormControl>
                </>
              )}

              {getIdNationality() && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Naturalidade (estado):</FormLabel>
                    <Select isRequired={false} {...register('state_naturality')}>
                      <option value="" hidden>
                        Selecione
                      </option>
                      {getStates.data?.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.initials}
                        </option>
                      ))}
                    </Select>
                    <MessageError>{errors.state_naturality?.message}</MessageError>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Naturalidade (cidade):</FormLabel>
                    <Select isDisabled={!watch('state_naturality')} isRequired={false} {...register('city_naturality')}>
                      <option value="" hidden>
                        Selecione
                      </option>
                      {getNaturality.data?.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </Select>
                    <MessageError>{errors.city_naturality?.message}</MessageError>
                  </FormControl>
                </>
              )}
            </Flex>
          </AccordionTemplate>

          {/* <AccordionTemplate display="flex" flexDir="column" gap={5} title="DADOS ADICIONAIS"></AccordionTemplate> */}

          <AccordionTemplate gapPanel={5} display="flex" flexDir="column" title="ENDEREÇO E CONTATO">
            <Flex mt={5} gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl w={['100%', '100%', '100%', '30%', '30%']} isRequired={!verifyIsForeign()}>
                <FormLabel>CEP:</FormLabel>
                <Flex>
                  <Input
                    isRequired={false}
                    borderTopRightRadius={0}
                    borderBottomRightRadius={0}
                    {...register('address_zip_code')}
                    as={inputMask}
                    mask="99999-999"
                    borderRight="none"
                  />

                  <Button onClick={checkCep} bg="gray.200" borderTopLeftRadius={0} borderBottomLeftRadius={0}>
                    <IoSearchSharp size={30} />
                  </Button>
                </Flex>
                <MessageError>{errors.address_zip_code?.message}</MessageError>
              </FormControl>
              <FormControl w={['100%', '100%', '100%', '70%', '70%']} isRequired={!verifyIsForeign()}>
                <FormLabel>Endereço:</FormLabel>
                <Input isRequired={false} {...register('address_street')} />
                <MessageError>{errors.address_street?.message}</MessageError>
              </FormControl>
            </Flex>

            <Flex gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl w={['100%', '100%', '100%', '30%', '30%']} isRequired={!verifyIsForeign()}>
                <FormLabel>Número:</FormLabel>
                <Input isRequired={false} {...register('address_number')} />
                <MessageError>{errors.address_number?.message}</MessageError>
              </FormControl>

              <FormControl w={['100%', '100%', '100%', '30%', '30%']}>
                <FormLabel>Complemento:</FormLabel>
                <Input {...register('address_complement')} />
                <MessageError>{errors.address_complement?.message}</MessageError>
              </FormControl>

              <FormControl w={['100%', '100%', '100%', '30%', '30%']} isRequired={!verifyIsForeign()}>
                <FormLabel>Bairro:</FormLabel>
                <Input isRequired={false} {...register('address_neighborhood')} />
                <MessageError>{errors.address_neighborhood?.message}</MessageError>
              </FormControl>
            </Flex>

            <Flex gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl w={['100%', '100%', '100%', '50%', '50%']} isRequired={!verifyIsForeign()}>
                <FormLabel>UF:</FormLabel>
                <Select isRequired={false} {...register('address_state')}>
                  <option value={''} hidden>
                    Selecione
                  </option>
                  {getStates?.data?.map((state) => (
                    <option value={state.id} key={state.id}>
                      {state.initials}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.address_state?.message}</MessageError>
              </FormControl>
              <FormControl w={['100%', '100%', '100%', '50%', '50%']} isRequired={!verifyIsForeign()}>
                <FormLabel>Cidade:</FormLabel>
                <Select isDisabled={!watch('address_state')} isRequired={false} {...register('address_city')}>
                  <option value={''} hidden>
                    Selecione
                  </option>
                  {getCity?.data?.map((city) => (
                    <option value={city.id} key={city.id}>
                      {city.name}
                    </option>
                  ))}
                </Select>
                <MessageError>{errors.address_city?.message}</MessageError>
              </FormControl>
            </Flex>
            <Flex gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <FormControl isRequired={!verifyIsForeign()} w={['100%', '100%', '100%', '50%', '50%']}>
                <FormLabel>Celular:</FormLabel>
                <Input
                  isRequired={false}
                  as={inputMask}
                  maskChar={''}
                  mask={maskPhone ? '(99) 99999-9999' : '(99) 9999-99999'}
                  {...register('cellphone', {
                    onChange: (ev: ChangeEvent<HTMLInputElement>) => {
                      if (ev?.currentTarget?.value && ev?.currentTarget?.value?.length >= 15) {
                        setMaskPhone(true);
                      }
                    },
                  })}
                />
                <MessageError>{errors.cellphone?.message}</MessageError>
              </FormControl>
              <FormControl w={['100%', '100%', '100%', '50%', '50%']}>
                <FormLabel>Telefone:</FormLabel>
                <Input as={inputMask} mask={'(99) 9999-9999'} {...register('phone')} />
              </FormControl>
            </Flex>
          </AccordionTemplate>

          <AccordionTemplate display="flex" flexDir="column" gap={5} title="DADOS DE ACESSO">
            <Flex mt={5} gap={5} flexDir={['column', 'column', 'column', 'row', 'row']}>
              <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                <InputTemplate label="E-mail:" isRequired={true} {...register('email')} />
                <MessageError>{errors.email?.message}</MessageError>
              </Stack>
              <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                <InputTemplate
                  isRequired={true}
                  {...register('password')}
                  label="Senha:"
                  icon={<IoIosEyeOff size={30} />}
                  type="password"
                />
                <MessageError>{errors.password?.message}</MessageError>
              </Stack>
              <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                <InputTemplate
                  onPasteCapture={(e) => e.preventDefault()}
                  isRequired={true}
                  {...register('confirm_password')}
                  label="Repita sua senha:"
                  icon={<IoIosEyeOff size={30} />}
                  type="password"
                />
                <MessageError>{errors.confirm_password?.message}</MessageError>
              </Stack>
            </Flex>
          </AccordionTemplate>

          <Stack flexDir="row" justify="flex-end" mt={10}>
            <DefaultButton bg="lightGrey" color="black" onClick={backButton} label="Cancelar" />

            <DefaultButton
              isLoading={isPending}
              bg="colorLink"
              color="white"
              type="submit"
              label="Finalizar"
              maxW="fit-content"
            />
          </Stack>
        </Accordion>
      </MainCard>
    </>
  );
};

export default SignUp;
